import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import acApi from 'axios/ac/acApi';
import { Transaction } from 'types/rest/assetCatalogTypes';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { useIsMount } from 'hooks/useIsMountHook';
import MixpanelService from 'utils/mixpanelService';
import {
  VIEW_TRANSACTION_VERSION,
  VIEW_TRANSACTION_VERSION_DIFF,
  VIEW_TRANSACTION_VERSION_HISTORY,
} from 'utils/mixpanelEvents';
import { TransactionVersionDetail, TransactionVersionSummary } from './transactionVersionHistoryTypes';
import { t } from './versionHistoryUtils';
import TransactionVersionHistoryList from './TransactionVersionHistoryList';
import TransactionVersionDiffView from './TransactionVersionDiffView';

/**
 * MOB-47723 — Transaction version history sketch (HTTP only).
 * Lists versions of the current transaction; "View" opens a past version as a normal (read-only)
 * transaction in a modal, "Restore" shows a diff vs. the current version with a "Save as latest" action.
 * Wired to the real backend version endpoints; kept intentionally lightweight for backend testing.
 */

interface Props {
  transaction: Transaction;
  /** Renders a read-only editor for a past version (supplied by TransactionEditor to avoid a circular import). */
  renderVersionEditor: (versionTransaction: Transaction) => React.ReactNode;
}

const TransactionVersionHistoryTab: React.FC<Props> = ({ transaction, renderVersionEditor }) => {
  const dispatch = useDispatch();
  const [versions, setVersions] = useState<TransactionVersionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState<'list' | 'diff'>('list');
  const [diffVersion, setDiffVersion] = useState<number | null>(null);
  const [viewTransaction, setViewTransaction] = useState<Transaction | null>(null);
  const [viewVersion, setViewVersion] = useState<number | null>(null);
  const isMount = useIsMount();

  const loadVersions = () => {
    setLoading(true);
    acApi.transactions
      .getTransactionVersions(transaction.id)
      .then(response => {
        setVersions(response.data.result || []);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadVersions();
    setMode('list');
    if (isMount) {
      MixpanelService.track(VIEW_TRANSACTION_VERSION_HISTORY());
    }
    // transaction.updated changes whenever the transaction is saved (e.g. via the editor's Save
    // button) — reload so a freshly saved version shows up in the history list right away.
  }, [transaction.id, transaction.updated]);

  const openView = (versionNumber: number) => {
    MixpanelService.track(VIEW_TRANSACTION_VERSION(versionNumber));
    acApi.transactions
      .getTransactionVersion(transaction.id, versionNumber)
      .then(response => {
        const detail: TransactionVersionDetail = response.data.result;
        const versionTransaction: any = _.cloneDeep(transaction);
        versionTransaction.name = detail.name;
        versionTransaction.description = detail.description;
        versionTransaction.dsl = detail.dsl;
        versionTransaction.sampleBody = detail.sampleBody;
        versionTransaction.sqlHint = detail.sqlHint;
        setViewVersion(versionNumber);
        setViewTransaction(versionTransaction);
      })
      .catch(() => dispatch(showToastNotification(ToastType.Danger, t('loadFailed'))));
  };

  const closeView = () => {
    setViewTransaction(null);
    setViewVersion(null);
  };

  const openDiff = (versionNumber: number) => {
    MixpanelService.track(VIEW_TRANSACTION_VERSION_DIFF(versionNumber));
    setDiffVersion(versionNumber);
    setMode('diff');
  };

  // From the read-only view modal: close it and open the diff (restore confirmation) on the History tab
  const openRestoreFromView = () => {
    const version = viewVersion;
    closeView();
    if (version !== null) {
      openDiff(version);
    }
  };

  const backToList = () => {
    setMode('list');
    setDiffVersion(null);
  };

  const onRestored = () => {
    backToList();
    loadVersions();
  };

  const headerContent = (
    <>
      <h4 className="ml-4 mt-4">{t('tab')}</h4>
      <p className="ml-4">{t('description')}</p>
    </>
  );

  if (loading) {
    return (
      <div className="bg-light p-2">
        {headerContent}
        <div className="p-4 text-center">
          <Spinner animation="border" size="sm" /> {t('loading')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light p-2">
        {headerContent}
        <div className="p-4 text-danger">{t('loadFailed')}</div>
      </div>
    );
  }

  const viewModal = (
    <Modal show={!!viewTransaction} onHide={closeView} dialogClassName="modal-full-screen">
      <Modal.Header closeButton>
        <Modal.Title>{t('viewTitle', { version: viewVersion })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{viewTransaction && renderVersionEditor(viewTransaction)}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeView}>
          {t('close')}
        </Button>
        <Button variant="primary" onClick={openRestoreFromView}>
          {t('restore')}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <>
      {viewModal}
      {mode === 'diff' && diffVersion !== null ? (
        <TransactionVersionDiffView
          transactionId={transaction.id}
          diffVersion={diffVersion}
          headerContent={headerContent}
          onBackToList={backToList}
          onRestored={onRestored}
        />
      ) : (
        <TransactionVersionHistoryList
          versions={versions}
          headerContent={headerContent}
          onView={openView}
          onRestore={openDiff}
        />
      )}
    </>
  );
};

export default TransactionVersionHistoryTab;
